import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Decimal } from 'decimal.js';
import { Alert, Button, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import WalletOutlined from '@mui/icons-material/WalletOutlined';
// Components
import { WalletInfo, InsufficientFundsAlert, InsufficientGasAlert } from './components';
import { AlertBody } from '@/common/index';
import MetaMaskButton from '@/components/ExternalWallets/MetaMask/MetaMaskButton';
import WalletModal from '../WalletModal/WalletModal';
// Hooks
import { useCartContext } from '@/providers/CartProvider';
import { useCheckoutContext } from '@/providers/CheckoutProvider';
import { useWallet } from '@/hooks/useWallet';
import { useCurrency } from '@/providers/CurrencyProvider';
// Utils
import { brand } from '@/brand/brand';
import { requireValidPrice } from '@/utils/coin';
import { formatAmount } from '@/utils/index';
import { routes } from '@/routes/routes';
import { isEvmCryptoAsset } from '@nerdcoresdk/foundations-crypto';
// Styles & Assets
import mmIcon from '@/icons/logos/metamask_logo.svg';
import styles from './wallet.module.css';

const metaMaskInfo = {
  icon: mmIcon.src,
  brandName: 'MetaMask',
};

export default function Wallet({
  connectionPending,
  downloadNeeded,
  ewError,
  gasFee,
  getBalance,
  handleConnectWallet,
  injectedWalletActive,
  resetEwError,
  resetEwState,
  resetMetamask,
  setAddFunds,
  setConnectionPending,
  walletInfo,
}) {
  const router = useRouter();
  const { conversionError, selectedPaymentAsset } = useCurrency();
  const {
    findFeeCoinFor,
    coreWalletCoinState,
    walletCoinState,
    walletCoins,
    hasDuplicateWallets,
  } = useWallet();
  const { cartTotals, cartDisableCheckout } = useCartContext();
  const {
    couponState,
    checkoutState,
    setUseExternalWallet,
    setUseMetaMask,
    useMetaMask,
    updateProgressBar,
    updateStep,
  } = useCheckoutContext();

  // MetaMask State
  const [extWalletError, setExtWalletError] = useState(false); // ewError prop is for connection info state errors, this is for insufficient funds
  const [extWallet, setExtWallet] = useState({
    show: false,
    walletId: '',
    coinAmt: 0,
    usd: 0,
  }); // when not enough funds, show wallet data for the MetaMask that is connected
  const [showDownloadModal, setShowDownloadModal] = useState(false); // if no MetaMask extension can be found or is mobile

  const walletInfoLegacy = selectedPaymentAsset
    ? walletCoins.find(
        (coin) => coin?.cryptoAsset?.uniqueKey === selectedPaymentAsset.uniqueKey
      )
    : null;

  const hasCoreWallet = coreWalletCoinState.hasWallet;
  const hasLegacyWallet = walletCoinState.hasWallet;
  const hasWallet = hasCoreWallet || hasLegacyWallet;
  const walletVersion = hasCoreWallet ? '2.0' : '';
  const feeCoin = findFeeCoinFor(selectedPaymentAsset);
  const feeWalletBalance = feeCoin?.coinAmt ?? 0;
  const isNativePayment =
    isEvmCryptoAsset(selectedPaymentAsset) && selectedPaymentAsset.traits.isNative;

  const formattedAvailableFeeBalance = formatAmount(feeWalletBalance, 6);

  const hasInsufficientGas = Decimal(feeWalletBalance).lt(gasFee.coin);
  const hasInsufficientFunds = Decimal(walletInfo?.usd ?? 0).lt(
    cartTotals.cartSubtotal -
      (couponState.smartNodeReqMet ? couponState.couponTotalUsd : 0)
  );
  const insufficientError = hasInsufficientGas || hasInsufficientFunds;

  useEffect(() => {
    if (!useMetaMask) return;
    handleConnectWallet('MetaMask');
  }, [useMetaMask]);

  useEffect(() => {
    if (!isNativePayment) {
      setExtWallet({ show: false });
      return;
    }
    // when the user connects to MetaMask verify the balance against the invoice
    injectedWalletActive &&
      getBalance(selectedPaymentAsset).then((data) => {
        setConnectionPending(false);
        if (!data) return setUseMetaMask(false);
        const { id, balance } = data;
        const usd = +Decimal.mul(balance, requireValidPrice(walletInfo.priceUsd));

        setExtWallet({
          show: true,
          walletId: id,
          coinAmt: balance,
          cryptoAsset: walletInfo.cryptoAsset || null,
          usd,
        });
        if (usd < cartTotals?.cartSubtotal) {
          setExtWalletError(true);
        } else {
          setExtWalletError(false);
        }
      });
  }, [injectedWalletActive, isNativePayment, selectedPaymentAsset?.uniqueKey]);

  // watch for MetaMask errors
  useEffect(() => {
    if (ewError.error) {
      return handleResetMetamask();
    }
    downloadNeeded && setShowDownloadModal(true);
  }, [ewError, downloadNeeded]);

  const isDisabled = conversionError || checkoutState.error.error || insufficientError;
  const isMMDisabled = useMetaMask ? connectionPending || extWalletError : false;

  // User manual external wallet payment
  const handleExternalPayment = () => {
    setUseExternalWallet(true);
    updateProgressBar();
    updateStep('next');
    !injectedWalletActive && resetEwState();
  };

  const handleMetaMaskClick = async () => {
    resetEwError();
    setConnectionPending(true);
    setUseMetaMask(true);
  };

  const handleResetMetamask = () => {
    resetMetamask();
    setConnectionPending(false);
    setUseMetaMask(false);
    setShowDownloadModal(false);
    setExtWalletError(false);
  };

  const handlePayBrandWallet = () => {
    resetEwState();
    setUseMetaMask(false);
    setUseExternalWallet(false);
    updateStep('next');
    updateProgressBar();
  };

  return (
    <div>
      <Typography
        variant="h3"
        className={styles.sectionHeader}
        data-core-qa="fundWalletTitleText"
      >
        Wallet
      </Typography>
      {hasInsufficientGas && hasWallet ? (
        <InsufficientGasAlert
          walletVersion={walletVersion}
          feeCoinSymbol={feeCoin?.cryptoAsset?.display.symbol || ''}
          formattedAvailableFeeBalance={formattedAvailableFeeBalance}
        />
      ) : null}
      {hasInsufficientFunds && hasWallet ? (
        <InsufficientFundsAlert
          walletVersion={walletVersion}
          walletInfo={walletInfo}
          hasDuplicateWallets={hasDuplicateWallets}
          brandName={brand.brandName}
          walletInfoLegacy={walletInfoLegacy}
        />
      ) : null}
      {extWallet.show && (extWalletError || ewError?.error) ? (
        <Alert severity={extWalletError ? 'error' : 'info'} variant="outlined">
          <AlertBody
            header={extWalletError ? 'Insufficient Funds.' : ewError.heading}
            body={
              extWalletError
                ? `Your MetaMask wallet doesn't have enough ${selectedPaymentAsset?.display.symbol} to complete this transaction. Please add funds and refresh the page, or select a different payment method.`
                : ewError.message
            }
          />
        </Alert>
      ) : null}
      {walletInfo?.walletId ? (
        <WalletInfo
          brand={brand}
          insufficientError={insufficientError}
          isDisabled={isDisabled}
          handleSubmit={handlePayBrandWallet}
          setAddFunds={setAddFunds}
          walletInfo={walletInfo}
          cartDisableCheckout={cartDisableCheckout}
        />
      ) : (
        <Button
          type="button"
          variant="contained"
          size="large"
          onClick={() => {
            router.push(routes.setupWallet.path);
          }}
          sx={{ marginBottom: '16px' }}
          data-core-qa="fundWalletButtonSetup"
          fullWidth
          startIcon={<Add />}
        >
          {`Setup ${brand.brandName} Wallet`}
        </Button>
      )}
      {extWallet.show && (
        <WalletInfo
          brand={metaMaskInfo}
          walletInfo={extWallet}
          insufficientError={extWalletError}
          handleSubmit={handleExternalPayment}
          isDisabled={isMMDisabled}
        />
      )}
      <div className={styles.buttonRow}>
        {!extWallet.show && isNativePayment && (
          <MetaMaskButton
            data-core-qa="fundWalletButtonMetamask"
            onClick={handleMetaMaskClick}
            disabled={isMMDisabled || cartDisableCheckout}
          >
            Connect MetaMask Wallet
          </MetaMaskButton>
        )}
        <Button
          type="button"
          variant="outlined"
          size="large"
          onClick={handleExternalPayment}
          data-core-qa="fundWalletButtonExternal"
          fullWidth
          startIcon={<WalletOutlined />}
          disabled={conversionError || cartDisableCheckout}
        >
          Pay with External Wallet
        </Button>
      </div>
      {showDownloadModal && (
        <WalletModal
          handleExternalPayment={handleExternalPayment}
          resetMetamask={handleResetMetamask}
          setShowDownloadModal={setShowDownloadModal}
          setConnectionPending={setConnectionPending}
        />
      )}
    </div>
  );
}
